import React from 'react';
import { Check, Minus } from 'lucide-react';
import { DatiAudioVerbale, RiepilogoAudio } from '../../services/verbaliAudioService';

type StatoTappa = 'fatta' | 'corrente' | 'saltata' | 'da_fare';

interface Tappa {
  titolo: string;
  nota: string;
  stato: StatoTappa;
}

interface VerbaleTappeProps {
  haContenuto: boolean;
  sezioniTotali: number;
  sezioniRiviste: number;
  bozza: boolean;
  pubblicatoAt: string | null;
  /** Registrazione e trascrizione usate (piano 2) senza i dati degli audio: «Salvata» / «Completata». */
  audioUsato?: boolean;
  /** Audio dell'evento (`GET /audio`, piano 2): stato vero delle prime due tappe. */
  audio?: DatiAudioVerbale | null;
  /** Una registrazione di questa scheda è in corso (o in pausa, o si sta chiudendo). */
  registrazioneAttiva?: boolean;
  /** Una registrazione di questa scheda è interrotta (da riprendere o terminare). */
  registrazioneInterrotta?: boolean;
  /** Voci riconosciute con certezza nelle trascrizioni (da `GET /voci`); assente ⇒ «Completata». */
  vociRiconosciute?: number | null;
}

/** `2 h 25 min`, `25 min`, `meno di 1 min`. */
export function durataBreve(ms: number): string {
  const minutiTotali = Math.floor(Math.max(0, ms || 0) / 60000);
  if (minutiTotali < 1) return 'meno di 1 min';
  const ore = Math.floor(minutiTotali / 60);
  const minuti = minutiTotali % 60;
  if (!ore) return `${minuti} min`;
  return minuti ? `${ore} h ${minuti} min` : `${ore} h`;
}

const STATI_SALVATI = new Set(['completa', 'in_trascrizione', 'trascritta', 'errore']);

/** Tappa «Registrazione» dagli audio: in corso, interrotta, salvata (con la durata) o non usata. */
function tappaRegistrazione(elenco: RiepilogoAudio[], attiva: boolean, interrotta: boolean): Tappa {
  const titolo = 'Registrazione';
  const aperte = elenco.filter((a) => a.stato === 'in_registrazione' || a.stato === 'in_caricamento');
  if (attiva || aperte.some((a) => a.stato === 'in_registrazione' && !a.interrottaAlle && !interrotta)) {
    return { titolo, nota: 'In corso', stato: 'corrente' };
  }
  if (interrotta || aperte.some((a) => a.interrottaAlle)) return { titolo, nota: 'Interrotta', stato: 'corrente' };
  const salvati = elenco.filter((a) => STATI_SALVATI.has(a.stato));
  if (salvati.length) {
    const durata = salvati.reduce((s, a) => s + (a.durataMs || 0), 0);
    return { titolo, nota: durata > 0 ? `${durataBreve(durata)} · salvata` : 'Salvata', stato: 'fatta' };
  }
  if (aperte.length) return { titolo, nota: 'In corso', stato: 'corrente' };
  return { titolo, nota: 'Non usata', stato: 'saltata' };
}

/** Tappa «Trascrizione» dagli audio: in corso (tratti), errore, voci riconosciute, senza voci o non usata. */
function tappaTrascrizione(elenco: RiepilogoAudio[], vociRiconosciute: number | null | undefined, inAttesa: boolean): Tappa {
  const titolo = 'Trascrizione';
  const inCorso = elenco.filter((a) => a.stato === 'in_trascrizione' || a.stato === 'completa');
  if (inCorso.length) {
    const fatti = inCorso.reduce((s, a) => s + (a.avanzamento?.fatti || 0), 0);
    const totali = inCorso.reduce((s, a) => s + (a.avanzamento?.totali || 0), 0);
    return { titolo, nota: totali > 0 ? `In corso: ${fatti} su ${totali}` : 'In corso', stato: 'corrente' };
  }
  const trascritti = elenco.filter((a) => a.stato === 'trascritta');
  if (elenco.some((a) => a.stato === 'errore') && !trascritti.length) return { titolo, nota: 'Errore', stato: 'corrente' };
  if (trascritti.length) {
    if (trascritti.every((a) => a.senzaVoci)) return { titolo, nota: 'Senza voci', stato: 'fatta' };
    if (typeof vociRiconosciute === 'number' && vociRiconosciute > 0) {
      return { titolo, nota: vociRiconosciute === 1 ? '1 voce riconosciuta' : `${vociRiconosciute} voci riconosciute`, stato: 'fatta' };
    }
    return { titolo, nota: 'Completata', stato: 'fatta' };
  }
  // Audio ancora da chiudere (registrazione in corso o interrotta, caricamento a metà): parte dopo.
  if (inAttesa || elenco.length) return { titolo, nota: 'Dopo la registrazione', stato: 'da_fare' };
  return { titolo, nota: 'Non usata', stato: 'saltata' };
}

const notaRevisione = (riviste: number, totali: number) =>
  riviste === 1 ? `1 sezione su ${totali} rivista` : `${riviste} sezioni su ${totali} riviste`;

const dataBreve = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Le tappe del verbale, con lo stato derivato dai dati (nessuno stato proprio). */
export function calcolaTappe({
  haContenuto, sezioniTotali, sezioniRiviste, bozza, pubblicatoAt, audioUsato = false,
  audio: datiAudio = null, registrazioneAttiva = false, registrazioneInterrotta = false, vociRiconosciute = null,
}: VerbaleTappeProps): Tappa[] {
  const audio: StatoTappa = audioUsato ? 'fatta' : 'saltata';
  const elenco = datiAudio && Array.isArray(datiAudio.audio) ? datiAudio.audio : null;
  const conAudio = !!elenco || registrazioneAttiva || registrazioneInterrotta;
  const registrazione: Tappa = conAudio
    ? tappaRegistrazione(elenco || [], registrazioneAttiva, registrazioneInterrotta)
    : { titolo: 'Registrazione', nota: audioUsato ? 'Salvata' : 'Non usata', stato: audio };
  const trascrizione: Tappa = conAudio
    ? tappaTrascrizione(elenco || [], vociRiconosciute, registrazioneAttiva || registrazioneInterrotta)
    : { titolo: 'Trascrizione', nota: audioUsato ? 'Completata' : 'Non usata', stato: audio };
  const audioInCorso = registrazione.stato === 'corrente' || trascrizione.stato === 'corrente';
  const pubblicato = !bozza;
  let revisione: Tappa;
  if (pubblicato) {
    revisione = { titolo: 'Bozza e revisione', nota: notaRevisione(sezioniRiviste, sezioniTotali), stato: 'fatta' };
  } else if (haContenuto) {
    revisione = { titolo: 'Bozza e revisione', nota: notaRevisione(sezioniRiviste, sezioniTotali), stato: 'corrente' };
  } else {
    // Registrazione o trascrizione ancora in corso: la revisione viene dopo.
    revisione = { titolo: 'Bozza e revisione', nota: 'Da iniziare', stato: audioInCorso ? 'da_fare' : 'corrente' };
  }
  const data = dataBreve(pubblicatoAt);
  return [
    registrazione,
    trascrizione,
    revisione,
    {
      titolo: 'Pubblicazione',
      nota: pubblicato ? (data ? `Pubblicato il ${data}` : 'Pubblicato') : 'Agli invitati',
      stato: pubblicato ? 'fatta' : 'da_fare',
    },
  ];
}

const STILE_CARTA: Record<StatoTappa, string> = {
  fatta: 'border-emerald-200 bg-white dark:border-emerald-800/60 dark:bg-gray-800',
  corrente: 'border-emerald-400 bg-white ring-4 ring-emerald-500/15 dark:border-emerald-500 dark:bg-gray-800',
  saltata: 'border-dashed border-slate-200 bg-slate-50/70 dark:border-gray-700 dark:bg-gray-800/50',
  da_fare: 'border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800',
};

const STILE_NUMERO: Record<StatoTappa, string> = {
  fatta: 'bg-emerald-600 text-white',
  corrente: 'border-2 border-emerald-600 bg-white text-emerald-700 dark:bg-gray-900 dark:text-emerald-300',
  saltata: 'bg-slate-100 text-slate-400 dark:bg-gray-700 dark:text-gray-500',
  da_fare: 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-gray-300',
};

const VerbaleTappe: React.FC<VerbaleTappeProps> = (props) => {
  const tappe = calcolaTappe(props);
  return (
    <ol className="grid grid-cols-2 gap-2 lg:grid-cols-4" aria-label="Avanzamento del verbale">
      {tappe.map((t, i) => (
        <li
          key={t.titolo}
          aria-current={t.stato === 'corrente' ? 'step' : undefined}
          className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${STILE_CARTA[t.stato]}`}
        >
          <span className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-full text-xs font-bold ${STILE_NUMERO[t.stato]}`}>
            {t.stato === 'fatta' ? <Check className="h-3.5 w-3.5" aria-hidden="true" />
              : t.stato === 'saltata' ? <Minus className="h-3.5 w-3.5" aria-hidden="true" />
                : i + 1}
          </span>
          <span className="min-w-0">
            <span className={`block truncate text-sm font-semibold ${t.stato === 'saltata' ? 'text-slate-400 dark:text-gray-500' : 'text-slate-800 dark:text-gray-100'}`}>
              {t.titolo}
            </span>
            <span className="block truncate text-xs text-slate-500 dark:text-gray-400">{t.nota}</span>
          </span>
        </li>
      ))}
    </ol>
  );
};

export default VerbaleTappe;
